import { LooplaySDKError } from '../errors';
import {
  type AuthEventName,
  type AuthLoginParams,
  type AuthProvider,
  type AuthStorage,
  type LooplayAuthOptions,
  type LooplaySession
} from './types';
import { MemoryAuthStorage } from './storage';

class InvalidSessionError extends LooplaySDKError {
  constructor() {
    super('Invalid session returned by auth provider');
    this.name = 'InvalidSessionError';
  }
}

type Listener = (session: LooplaySession | null) => void;

export class LooplayAuth {
  private readonly provider: AuthProvider;
  private readonly storage: AuthStorage;
  private readonly key: string;

  private session: LooplaySession | null = null;
  private readonly listeners = new Set<Listener>();

  constructor(options: LooplayAuthOptions) {
    this.provider = options.provider;
    this.storage = options.storage ?? new MemoryAuthStorage();
    this.key = `${options.storageKeyPrefix ?? 'looplay'}:auth:session`;
  }

  async init(): Promise<void> {
    await this.provider.init?.();
    await this.restoreFromStorage();

    // Keep provider in sync with restored session (if any).
    await this.provider.onSessionChanged?.(this.session);
  }

  getSession(): LooplaySession | null {
    return this.session;
  }

  async login(params?: AuthLoginParams): Promise<LooplaySession> {
    const result = await this.provider.login(params);

    if (!result?.session?.user?.id) {
      throw new InvalidSessionError();
    }

    await this.setSession(result.session, 'login');
    return result.session;
  }

  async logout(): Promise<void> {
    await this.provider.logout();
    await this.setSession(null, 'logout');
  }

  async getAccessToken(): Promise<string | undefined> {
    if (!this.session) return undefined;

    const now = Date.now();
    const isExpired =
      typeof this.session.expiresAt === 'number' && this.session.expiresAt <= now;

    if (!isExpired) return this.session.accessToken;

    if (!this.provider.refresh) return this.session.accessToken;

    const refreshed = await this.provider.refresh();
    const next = { ...this.session, ...refreshed };
    await this.setSession(next, 'sessionChanged');

    return next.accessToken;
  }

  on(eventName: AuthEventName, listener: Listener): () => void {
    // eventName reserved for future filtering; for now notify on any change
    void eventName;

    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private async restoreFromStorage(): Promise<void> {
    const raw = await this.storage.getItem(this.key);
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw) as LooplaySession;
      if (parsed?.user?.id) {
        this.session = parsed;
      }
    } catch {
      // ignore corrupted cache
      await this.storage.removeItem(this.key);
    }
  }

  private async setSession(next: LooplaySession | null, event: AuthEventName): Promise<void> {
    this.session = next;

    await this.provider.onSessionChanged?.(this.session);

    if (next) {
      await this.storage.setItem(this.key, JSON.stringify(next));
    } else {
      await this.storage.removeItem(this.key);
    }

    for (const listener of this.listeners) {
      listener(this.session);
    }

    // reserved for future event-specific hooks
    void event;
  }
}
